import {
  createUserWithEmailAndPassword,
  getRedirectResult,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider } from './clients';
import { wrapSubscriptionError } from '../../services/subscriptionError';
import type { UserRoles } from '../../domain/identity/model';

export type AuthUser = FirebaseUser;
export type GoogleSignInMode = 'popup' | 'redirect';
export type Unsubscribe = () => void;

export async function getRedirectUser(): Promise<AuthUser | null> {
  const result = await getRedirectResult(auth);
  return result?.user ?? null;
}

export function subscribeToAuthState(onChange: (user: AuthUser | null) => void): Unsubscribe {
  return onAuthStateChanged(auth, onChange);
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName: string,
): Promise<AuthUser> {
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(user, { displayName });
  return user;
}

export function signInWithEmail(email: string, password: string): Promise<unknown> {
  return signInWithEmailAndPassword(auth, email, password);
}

export function signInWithGoogle(mode: GoogleSignInMode): Promise<unknown> {
  if (mode === 'redirect') return signInWithRedirect(auth, googleProvider);
  return signInWithPopup(auth, googleProvider);
}

export function logout(): Promise<void> {
  return signOut(auth);
}

export function resetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(auth, email);
}

export function subscribeToUserRoles(
  uid: string,
  onChange: (roles: UserRoles) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const userRef = doc(db, 'users', uid);
  return onSnapshot(
    userRef,
    (snapshot) => {
      const data = snapshot.data();
      if (!data) return;
      const isAdmin = data.isAdmin === true;
      onChange({
        isAdmin,
        isReviewer: isAdmin || data.isReviewer === true,
        reviewCampaignIds: Array.isArray(data.reviewCampaignIds) ? data.reviewCampaignIds : [],
        trustedCampaignIds: Array.isArray(data.trustedCampaignIds) ? data.trustedCampaignIds : [],
        expertId: typeof data.expertId === 'string' ? data.expertId : null,
      });
    },
    wrapSubscriptionError('AuthContext.roleSubscription', onError),
  );
}
